import React from 'react';
import { motion } from 'framer-motion';
import { stats, services } from '../mock';

const About = () => {
  return (
    <section id="about" className="relative py-32 bg-background">
      <div className="absolute inset-0 bg-grid-dark opacity-10 pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-12 gap-16 relative">
        
        {/* Sticky Left Sidebar */}
        <div className="lg:col-span-5 relative">
          <div className="sticky top-32">
            <span className="mono text-xs uppercase tracking-widest text-primary mb-4 block">// 01 — Profile</span>
            <h2 className="serif text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.1] mb-8">
              Architecture <br />
              is a <span className="italic text-primary">conversation</span> <br />
              between business <br />
              and code.
            </h2>
            <div className="w-20 h-[1px] bg-white/20 mb-8" />
            <p className="text-slate-400 font-light text-sm sm:text-base leading-relaxed">
              I'm an Azure-focused Senior Cloud Solutions Architect with 20+ years of experience designing, migrating and optimizing enterprise cloud environments. My career spans on-premises datacenters, multi-cloud estates and modern PaaS — with deep specialization in large-scale migrations from On-prem, AWS and GCP to Azure.
            </p>
          </div>
        </div>

        {/* Scrollable Right Content */}
        <div className="lg:col-span-7">
          <div className="space-y-24">
            
            {/* Intro Text */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-lg sm:text-xl text-slate-300 font-light leading-relaxed mb-10">
                As an SME for <span className="text-primary italic">Azure Front Door, App Services, AKS, Landing Zones and IaC</span>, I bring rigor to architecture and humility to delivery — designing for the team that will run it on a Tuesday, not just the diagram that wins the deal.
              </p>
              
              <div className="flex flex-wrap gap-3">
                {['Azure', 'Cloud Architect', 'DevOps Architect', 'Enterprise Architect', 'MCT', 'Migrations', 'Landing Zones', 'IaC / Terraform', 'AKS', 'Front Door', 'Security', 'FinOps'].map((c, i) => (
                  <motion.span 
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.05 }}
                    key={c} 
                    className="px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md text-xs mono uppercase tracking-widest text-slate-400 hover:text-white hover:border-primary transition-colors cursor-default"
                  >
                    {c}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6">
              {stats.map((s, i) => (
                <motion.div 
                  key={i}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.6 }}
                  className="p-8 rounded-3xl bg-card border border-white/5 relative overflow-hidden group"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                  <div className="relative z-10">
                    <div className="serif text-5xl sm:text-6xl text-white mb-3 group-hover:text-primary transition-colors">{s.value}</div>
                    <div className="text-sm font-medium text-slate-300 mb-1">{s.label}</div>
                    <div className="text-xs text-slate-500">{s.sub}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Services List */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h3 className="serif text-3xl sm:text-4xl text-white mb-10">
                End-to-end cloud engagements, <br />
                <span className="italic text-slate-500">without the buzzwords.</span>
              </h3>
              
              <div className="space-y-6">
                {services.map((s, i) => (
                  <div key={i} className="group border-b border-white/10 pb-6">
                    <h4 className="serif text-xl sm:text-2xl text-white mb-3 group-hover:text-primary transition-colors">{s.title}</h4>
                    <p className="text-slate-400 font-light text-sm sm:text-base">{s.desc}</p>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
        
      </div>
    </section>
  );
};

export default About;
